/**
 * The Machinist: the deterministic half of the Systems Supervisor.
 *
 * It watches readings that are already on the desk, cheaply and continuously,
 * and turns them into findings a captain can act on: what is safe to run, what
 * is held back and why, what would be unwise, and what the evidence for each
 * of those is. Every finding names the row it came from and the moment that row
 * was taken. Nothing here invents a provider status, a limit, a percentage or
 * a system state.
 *
 * What the Machinist does NOT do, in code as well as in words:
 * it does not reroute pinned work, change the owner's limits, start or stop
 * Workers, kill running work because load is high, or dispatch anything at all.
 * This module returns a report. It has no client, no mutation and no write.
 *
 * AI reasoning stays bounded: `machinistChangeKey` and `shouldSummariseWithAi`
 * decide when a change is meaningful enough to be worth a model's attention.
 * Nothing on this page keeps a model running to render state.
 *
 * Pure: no I/O, no clock. The moment arrives as `nowMs`.
 */

import type { EngineRoomModel, EngineRoomState } from './engine-room';
import type { EngineRoomMachine } from './engine-room-machines';
import { machineAcceptsWork } from './engine-room-machines';
import { readingName, type HeldBackBy } from './capacity';
import type { SystemMonitorCard } from './systems-monitor';

export type MachinistSeverity = 'critical' | 'attention' | 'info';

export type MachinistTargetKind = 'machine' | 'ai' | 'system' | 'work';

/** Where clicking a finding takes the owner: an existing detail surface, never a new one. */
export interface MachinistTarget {
  readonly kind: MachinistTargetKind;
  readonly ref: string;
  readonly label: string;
}

export interface MachinistEvidence {
  readonly label: string;
  readonly value: string;
  readonly observedAt: string | null;
}

export interface MachinistFinding {
  readonly id: string;
  readonly rule: string;
  readonly severity: MachinistSeverity;
  readonly title: string;
  readonly impact: string;
  readonly recommendedAction: string;
  readonly evidence: readonly MachinistEvidence[];
  readonly target: MachinistTarget | null;
}

export interface MachinistReport {
  readonly state: EngineRoomState;
  /** One short captain-style line, built from the findings and nothing else. */
  readonly summary: string;
  readonly findings: readonly MachinistFinding[];
  /** Machines that could safely take more work right now, in plain words. */
  readonly safeToRun: readonly string[];
  /** Machines and lanes that are deliberately not taking work, with the reason. */
  readonly heldBack: readonly string[];
  /** The standing limits of this supervisor, restated with every report. */
  readonly boundaries: readonly string[];
}

/** The things the Machinist will never do, said out loud beside every report. */
export const MACHINIST_BOUNDARIES: readonly string[] = [
  'Does not reroute pinned work.',
  'Does not change the owner’s machine limits.',
  'Does not start or stop Workers outside existing policy.',
  'Does not kill running work because load is high.',
  'Does not state a provider or system status that no reading reports.',
] as const;

const SEVERITY_ORDER: Readonly<Record<MachinistSeverity, number>> = {
  critical: 0, attention: 1, info: 2,
};

function plural(count: number, one: string, many: string): string {
  return count === 1 ? one : many;
}

function machineTarget(machine: EngineRoomMachine): MachinistTarget {
  return { kind: 'machine', ref: machine.nodeId, label: machine.name };
}

function systemFact(card: SystemMonitorCard, id: string): string | null {
  const value = card.facts.find((fact) => fact.id === id)?.value ?? null;
  return value === null || value === 'Unknown' ? null : value;
}

/**
 * Whether starting another local model on this machine would be unwise.
 *
 * It fires only on measured evidence: a local runtime with something loaded,
 * and a machine that is either already at its limit, already held back by a
 * memory reading, or sitting at or below the reserve it keeps free.
 */
function localModelPressure(machine: EngineRoomMachine): boolean {
  const local = machine.localRuntime;
  if (local === null || !local.loadedModelsKnown || local.loadedModels.length === 0) return false;
  if (machine.readiness === 'held_back' && machine.heldBackBy !== null) return true;
  if (machine.readiness === 'at_limit') return true;
  const memory = machine.memoryHeadroomPercent;
  const video = machine.graphicsHeadroomPercent;
  return (memory !== null && memory <= 0) || (video !== null && video <= 0);
}

function machineFindings(machines: readonly EngineRoomMachine[]): MachinistFinding[] {
  const findings: MachinistFinding[] = [];
  for (const machine of machines) {
    const observedAt = machine.observedAt;
    if (machine.connection !== 'connected') {
      findings.push({
        id: `machine-out-of-touch:${machine.nodeId}`,
        rule: 'machine_out_of_touch',
        severity: machine.connection === 'disconnected' ? 'critical' : 'attention',
        title: `${machine.name} is ${machine.connection === 'disconnected' ? 'not reporting' : 'out of touch'}`,
        impact: `Nothing can be routed to ${machine.name}, and its readings are no longer current.`,
        recommendedAction: `Treat ${machine.name} as unavailable until it reports again. Do not plan work that needs it.`,
        evidence: [{ label: 'Last seen', value: machine.lastSeen, observedAt: machine.lastSeenAt }],
        target: machineTarget(machine),
      });
      continue;
    }
    if (machine.readiness === 'held_back' && machine.reason !== null) {
      const reading = machine.heldBackBy === null ? 'a reading' : readingName(machine.heldBackBy);
      // A sustained ceiling is the other way round from a reserve: too much in
      // use rather than too little left, and it lets go only once the average is
      // clear of the ceiling - so the advice names the ceiling, not a reserve.
      const guard = machine.heldBackBy === 'cpu' || machine.heldBackBy === 'graphics'
        ? machine.guards.find((item) => item.dimension === machine.heldBackBy) ?? null
        : null;
      const reserve = machine.heldBackBy === 'memory'
        ? machine.reserveMemoryPercent
        : machine.heldBackBy === 'video_memory' ? machine.reserveGraphicsPercent : null;
      const isGuard = machine.heldBackBy === 'cpu' || machine.heldBackBy === 'graphics';
      findings.push({
        id: `machine-held-back:${machine.nodeId}`,
        rule: 'machine_held_back',
        severity: 'attention',
        title: `${machine.name} is held back by ${reading}`,
        impact: isGuard
          ? `New work goes elsewhere while ${machine.name} stays over the ceiling you set. Work already running carries on.`
          : `New work goes elsewhere while ${machine.name} is below the share it keeps free.`,
        recommendedAction: isGuard
          ? guard?.ceilingPercent == null
            ? `Leave ${machine.name} held back until its sustained load falls back under the ceiling.`
            : `Leave ${machine.name} held back until ${reading} falls clear of the ${guard.ceilingPercent}% ceiling.`
          : reserve === null
            ? `Leave ${machine.name} held back until a later reading says otherwise.`
            : `Leave ${machine.name} held back until ${reading} is above ${reserve}%.`,
        evidence: [
          { label: 'Engine reason', value: machine.reason, observedAt },
          ...(guard === null ? [] : [{ label: guard.label, value: guard.line, observedAt: guard.changedAt ?? observedAt }]),
        ],
        target: machineTarget(machine),
      });
    }
    if (machine.readiness === 'at_limit') {
      findings.push({
        id: `machine-at-limit:${machine.nodeId}`,
        rule: 'machine_at_limit',
        severity: 'attention',
        title: `${machine.name} is at its limit`,
        impact: `${machine.name} takes no further Worker until a place frees up.`,
        recommendedAction: 'Queue the work or route it to a machine with a place free.',
        evidence: [{
          label: 'Places',
          value: machine.seats === null ? 'Unknown' : `${machine.activeRuns ?? 0} of ${machine.seats} in use`,
          observedAt,
        }],
        target: machineTarget(machine),
      });
    }
    if (machineAcceptsWork(machine) && machine.placesFree !== null && machine.placesFree > 0) {
      const count = machine.placesFree;
      findings.push({
        id: `machine-capacity:${machine.nodeId}`,
        rule: 'machine_capacity',
        severity: 'info',
        title: `${machine.name} is safe for ${count} more ${plural(count, 'Worker', 'Workers')}`,
        impact: `${machine.name} can take new work now without crossing the share it keeps free.`,
        recommendedAction: `Route unpinned work to ${machine.name} first.`,
        evidence: [{ label: 'Capacity', value: machine.readinessLine, observedAt }],
        target: machineTarget(machine),
      });
    }
    if (machine.stale) {
      findings.push({
        id: `machine-stale:${machine.nodeId}`,
        rule: 'machine_reading_stale',
        severity: 'attention',
        title: `${machine.name}'s reading is ${machine.readingAge ?? 'old'}`,
        impact: 'The capacity shown for this machine may no longer be what it is under.',
        recommendedAction: `Treat ${machine.name}'s figures as provisional until it reports again.`,
        evidence: [{ label: 'Reading taken', value: machine.readingAge ?? 'Unknown', observedAt }],
        target: machineTarget(machine),
      });
    }
    if (machine.versionBehind === true) {
      findings.push({
        id: `machine-version:${machine.nodeId}`,
        rule: 'node_version_behind',
        severity: 'attention',
        title: `${machine.name} is behind the published node release`,
        impact: 'Behaviour on this machine may not match what current main expects.',
        recommendedAction: `Update ${machine.name} before relying on anything the newer node adds.`,
        evidence: [{
          label: 'Version',
          value: `running ${machine.runningVersion ?? 'Unknown'}, target ${machine.targetVersion ?? 'Unknown'}`,
          observedAt: machine.lastSeenAt,
        }],
        target: machineTarget(machine),
      });
    }
    if (localModelPressure(machine)) {
      const loaded = machine.localRuntime?.loadedModels.join(', ') ?? 'a local model';
      findings.push({
        id: `machine-local-model:${machine.nodeId}`,
        rule: 'local_model_pressure',
        severity: 'attention',
        title: `Do not start another local model on ${machine.name}`,
        impact: `${machine.name} is already holding ${loaded} while it is ${machine.readinessPhrase}.`,
        recommendedAction: `Wait until ${machine.name} reports room, or run the model on a machine that has it.`,
        evidence: [
          { label: 'Loaded locally', value: loaded, observedAt },
          { label: 'Machine', value: machine.readinessLine, observedAt },
        ],
        target: machineTarget(machine),
      });
    }
    if (machine.recentFailures >= 2) {
      findings.push({
        id: `machine-failures:${machine.nodeId}`,
        rule: 'repeated_failures',
        severity: 'attention',
        title: `${machine.name} has ${machine.recentFailures} recent failed runs`,
        impact: 'Repeated failures on one machine usually mean its environment, not the task.',
        recommendedAction: `Read the failed runs from ${machine.name} before sending it more of the same work.`,
        evidence: [{
          label: 'Recent runs',
          value: `${machine.recentFailures} of ${machine.recentRuns} failed`,
          observedAt: null,
        }],
        target: machineTarget(machine),
      });
    }
  }
  return findings;
}

function systemFindings(systems: readonly SystemMonitorCard[]): MachinistFinding[] {
  const findings: MachinistFinding[] = [];
  for (const card of systems) {
    if (card.status === 'Failing') {
      findings.push({
        id: `system-failing:${card.typeKey}`,
        rule: 'system_failing',
        severity: 'critical',
        title: `${card.label} reports a failure`,
        impact: card.label === 'Cloudflare'
          ? 'Production web may not match current main.'
          : `Work that depends on ${card.label} may not behave as the repository expects.`,
        recommendedAction: `Read the ${card.label} evidence before starting work that depends on it.`,
        evidence: [{ label: card.source, value: card.summary, observedAt: card.observedAt }],
        target: { kind: 'system', ref: card.typeKey, label: card.label },
      });
    } else if (card.status === 'Attention' || card.status === 'Unavailable') {
      findings.push({
        id: `system-attention:${card.typeKey}`,
        rule: 'system_attention',
        severity: 'attention',
        title: `${card.label} needs attention`,
        impact: card.status === 'Unavailable'
          ? `Brunel has no supported secure source for ${card.label} right now.`
          : `${card.label} is not reporting a clean state.`,
        recommendedAction: `Check ${card.label} in the Connector Center before depending on it.`,
        evidence: [{ label: card.source, value: card.summary, observedAt: card.observedAt }],
        target: { kind: 'system', ref: card.typeKey, label: card.label },
      });
    }
    const migration = systemFact(card, 'migration_status');
    if (migration !== null && migration.toLowerCase().includes('behind')) {
      findings.push({
        id: `system-migration:${card.typeKey}`,
        rule: 'migration_behind',
        severity: 'attention',
        title: `${card.label} migration state is behind current main`,
        impact: 'Schema-dependent work may run against a database the repository has already moved past.',
        recommendedAction: 'Avoid schema-dependent work until the applied migration matches the expected one.',
        evidence: [
          { label: 'Expected migration', value: systemFact(card, 'migration_expected') ?? 'Unknown', observedAt: card.observedAt },
          { label: 'Applied migration', value: systemFact(card, 'migration_applied') ?? 'Unknown', observedAt: card.observedAt },
        ],
        target: { kind: 'system', ref: card.typeKey, label: card.label },
      });
    }
  }
  return findings;
}

function aiFindings(model: EngineRoomModel): MachinistFinding[] {
  const findings: MachinistFinding[] = [];
  for (const lane of model.ai.lanes) {
    if (lane.availability !== 'exhausted' && lane.availability !== 'unavailable') continue;
    findings.push({
      id: `ai-${lane.availability}:${lane.key}`,
      rule: lane.availability === 'exhausted' ? 'ai_exhausted' : 'ai_unavailable',
      severity: 'attention',
      title: `${lane.title} is ${lane.availability}`,
      impact: `Work pinned to ${lane.title} cannot start until it reports capacity again.`,
      recommendedAction: `Do not silently substitute another runtime for pinned ${lane.title} work. Leave it queued or ask the owner.`,
      evidence: [{ label: lane.meter.measurementLabel ?? 'Status', value: lane.meter.detailLine, observedAt: null }],
      target: { kind: 'ai', ref: lane.key, label: lane.title },
    });
  }
  const available = model.ai.lanes.filter((lane) => lane.availability === 'available');
  const spent = model.ai.lanes.filter((lane) => lane.availability === 'exhausted');
  if (available.length > 0 && spent.length > 0) {
    const preferred = available[0]!;
    findings.push({
      id: 'ai-preference',
      rule: 'ai_preference',
      severity: 'info',
      title: `${preferred.title} is available; ${spent.map((lane) => lane.title).join(', ')} ${plural(spent.length, 'is', 'are')} exhausted`,
      impact: 'Unpinned coding work can still start, on a lane that is reporting capacity.',
      recommendedAction: `Prefer ${preferred.title} for the next unpinned coding task. Pinned work stays pinned.`,
      evidence: [{ label: preferred.meter.measurementLabel ?? 'Status', value: preferred.meter.detailLine, observedAt: null }],
      target: { kind: 'ai', ref: preferred.key, label: preferred.title },
    });
  }
  return findings;
}

function workFindings(model: EngineRoomModel): MachinistFinding[] {
  const findings: MachinistFinding[] = [];
  const work = model.work;
  if (work.queuedCount > 0 && work.eligibleRoutes === 0) {
    findings.push({
      id: 'work-queue-pressure',
      rule: 'queue_pressure',
      severity: 'attention',
      title: `${work.queuedCount} ${plural(work.queuedCount, 'job is', 'jobs are')} waiting with nowhere to run`,
      impact: 'No connected computer currently has a place free, so nothing will start on its own.',
      recommendedAction: 'Free a place, or leave the queue until a machine reports room. Do not raise a limit to clear it.',
      evidence: [{
        label: 'Routing',
        value: work.routes.map((route) => `${route.nodeName}: ${route.reason}`).join('; ') || 'No computer reporting',
        observedAt: null,
      }],
      target: { kind: 'work', ref: 'queued', label: 'Work Board' },
    });
  }
  if (work.blockedCount > 0) {
    findings.push({
      id: 'work-blocked',
      rule: 'work_blocked',
      severity: 'attention',
      title: `${work.blockedCount} ${plural(work.blockedCount, 'piece', 'pieces')} of work ${plural(work.blockedCount, 'is', 'are')} blocked`,
      impact: 'Blocked work is waiting on an answer or an approval, not on capacity.',
      recommendedAction: 'Open the blocked work and answer what it is waiting for.',
      evidence: work.blocked.slice(0, 3).map((card) => ({
        label: card.title,
        value: card.blockerOrApproval ?? card.currentState,
        observedAt: null,
      })),
      target: { kind: 'work', ref: 'blocked', label: 'Work Board' },
    });
  }
  return findings;
}

/** The captain line's word for what is holding a machine back. */
const HELD_WORDS: Readonly<Record<HeldBackBy, string>> = {
  memory: 'memory-held',
  video_memory: 'video-memory-held',
  cpu: 'held by sustained CPU load',
  graphics: 'held by sustained GPU load',
  disk: 'held for low disk space',
};

/**
 * The captain's one line.
 *
 * Built only from findings that already exist, in a fixed order, and clipped.
 * Nothing is asserted here that is not asserted above it on the page.
 */
export function machinistSummary(model: EngineRoomModel): string {
  const parts: string[] = [];
  for (const machine of model.machines) {
    if (machine.connection !== 'connected') { parts.push(`${machine.name} is out of touch`); continue; }
    if (machine.readiness === 'held_back' && machine.heldBackBy !== null) {
      parts.push(`${machine.name} is ${HELD_WORDS[machine.heldBackBy]}`);
      continue;
    }
    if (machine.readiness === 'at_limit') { parts.push(`${machine.name} is at its limit`); continue; }
    if (machineAcceptsWork(machine) && machine.placesFree !== null && machine.placesFree > 0) {
      parts.push(`${machine.name} can accept ${machine.placesFree} more ${plural(machine.placesFree, 'Worker', 'Workers')}`);
      continue;
    }
    parts.push(`${machine.name} is ${machine.readinessPhrase}`);
  }
  const exhausted = model.ai.lanes.filter((lane) => lane.availability === 'exhausted');
  const available = model.ai.lanes.filter((lane) => lane.availability === 'available');
  if (available.length > 0) parts.push(`${available[0]!.title} available`);
  for (const lane of exhausted.slice(0, 2)) parts.push(`${lane.title} exhausted`);
  for (const card of model.infrastructure.systems) {
    if (card.status === 'Unknown') continue;
    parts.push(`${card.label} ${card.status.toLowerCase()}`);
  }
  if (parts.length === 0) return 'No current readings. The Engine Room has nothing to report.';
  return `${parts.join('. ')}.`;
}

export function runMachinist(model: EngineRoomModel): MachinistReport {
  const findings = [
    ...machineFindings(model.machines),
    ...systemFindings(model.infrastructure.systems),
    ...aiFindings(model),
    ...workFindings(model),
  ].sort((left, right) => SEVERITY_ORDER[left.severity] - SEVERITY_ORDER[right.severity]
    || left.id.localeCompare(right.id));

  const safeToRun = model.machines
    .filter((machine) => machineAcceptsWork(machine) && machine.placesFree !== null && machine.placesFree > 0)
    .map((machine) => `${machine.name}: ${machine.placesFree} ${plural(machine.placesFree ?? 0, 'place', 'places')} free`);
  const heldBack = model.machines
    .filter((machine) => !machineAcceptsWork(machine))
    .map((machine) => `${machine.name}: ${machine.connection === 'connected' ? machine.readinessLine : `out of touch, last seen ${machine.lastSeen}`}`);

  return {
    state: model.captain.state,
    summary: machinistSummary(model),
    findings,
    safeToRun,
    heldBack,
    boundaries: MACHINIST_BOUNDARIES,
  };
}

/* ---------------------------------------------------------------------------
 * Reporting upward: the Machinist tells the captain, it does not act.
 * ------------------------------------------------------------------------ */

export interface MachinistOverseerItem {
  readonly title: string;
  readonly evidence: string;
  readonly impact: string;
  readonly recommendedAction: string;
}

/**
 * The shape the Main Overseer / Main Command receives.
 *
 * It is an operational update with its evidence attached, not a command and
 * not a plan. It goes into the one existing Command Center conversation - this
 * never opens a second chat - and the owner decides what, if anything, follows.
 */
export interface MachinistOverseerReport {
  readonly kind: 'machinist_operational_update';
  readonly surface: 'main_command';
  readonly generatedAt: string;
  readonly state: EngineRoomState;
  readonly update: string;
  readonly items: readonly MachinistOverseerItem[];
  readonly boundaries: readonly string[];
}

/** How many findings travel upward. A captain wants the short version. */
export const MACHINIST_REPORT_LIMIT = 6;

export function machinistOverseerReport(report: MachinistReport, generatedAt: string): MachinistOverseerReport {
  return {
    kind: 'machinist_operational_update',
    surface: 'main_command',
    generatedAt,
    state: report.state,
    update: report.summary,
    items: report.findings
      .filter((finding) => finding.severity !== 'info')
      .slice(0, MACHINIST_REPORT_LIMIT)
      .map((finding) => ({
        title: finding.title,
        evidence: finding.evidence
          .map((item) => `${item.label}: ${item.value}${item.observedAt === null ? '' : ` (observed ${item.observedAt})`}`)
          .join('; ') || 'No timestamped evidence',
        impact: finding.impact,
        recommendedAction: finding.recommendedAction,
      })),
    boundaries: report.boundaries,
  };
}

/** The same report as the words the owner sends into the one Command Center. */
export function formatMachinistForMainCommand(report: MachinistOverseerReport): string {
  const lines = [
    'Machinist operational update. This is a report, not a request to run anything.',
    `Engine Room: ${report.state}. ${report.update}`,
  ];
  if (report.items.length === 0) {
    lines.push('Nothing currently needs attention.');
  } else {
    lines.push('', 'Findings:');
    for (const item of report.items) {
      lines.push(
        `- ${item.title}`,
        `  Evidence: ${item.evidence}`,
        `  Impact: ${item.impact}`,
        `  Recommended: ${item.recommendedAction}`,
      );
    }
  }
  lines.push('', `Taken at ${report.generatedAt}. ${report.boundaries.join(' ')}`);
  return lines.join('\n');
}

/* ---------------------------------------------------------------------------
 * Bounded AI: when a change is worth a model's attention, and when it is not.
 * ------------------------------------------------------------------------ */

/**
 * A stable key for what the deterministic layer currently believes.
 *
 * Two reports with the same key say the same thing about the ship, so the
 * second one is not worth interpreting. Figures that drift every reading -
 * percentages, ages, timestamps - are deliberately left out of the key.
 */
export function machinistChangeKey(report: MachinistReport): string {
  return [report.state, ...report.findings.map((finding) => `${finding.rule}:${finding.id}:${finding.severity}`)].join('|');
}

/** The shortest gap between two bounded AI summaries of the same engine room. */
export const MACHINIST_AI_MIN_INTERVAL_MS = 600_000;

export interface MachinistAiGate {
  readonly report: MachinistReport;
  readonly lastKey: string | null;
  readonly lastSummaryAtMs: number | null;
  readonly nowMs: number;
  readonly minIntervalMs?: number;
}

/**
 * Whether a bounded AI summary is warranted right now.
 *
 * Three conditions, all required: the deterministic picture has actually
 * changed, something in it matters, and enough time has passed since the last
 * time a model was asked. Anything less is rendered from the rules alone - the
 * Engine Room never keeps a model running to show its own state.
 */
export function shouldSummariseWithAi(gate: MachinistAiGate): boolean {
  const key = machinistChangeKey(gate.report);
  if (key === gate.lastKey) return false;
  if (!gate.report.findings.some((finding) => finding.severity !== 'info')) return false;
  const minInterval = gate.minIntervalMs ?? MACHINIST_AI_MIN_INTERVAL_MS;
  if (gate.lastSummaryAtMs !== null && gate.nowMs - gate.lastSummaryAtMs < minInterval) return false;
  return true;
}
